import {useOptionalMeasurementEditor} from "../pages/selection/useFanCriteria";
import {WentechMeasuringStandards} from "../business/measurement/WentechMeasurementStandards";
import {useEditor} from "../form/EditorReact";
import {OneOfEditor} from "../form/editors/OneOfEditor";
import {MetronicMultiButtonOneOfEditorXxs} from "../form/metronic/MetronicMultiButtonOneOfEditor";
import {createInternationalizationPrefixer} from "../platform/i18n/i18n";
import {SimulationRequestJson} from "../business/ApiJsonTypes";
import React, {useEffect, useRef, useState} from "react";
import {MechanicalConfigurator} from "./MechanicalConfigurator";
import {useShaftDimensionsEditors} from "./useShaftDimensionsEditors";
import {UsedConfigurationSummary} from "../business/useImpellerDatasheet";
import {useSyncShaftPreferencesToSelectionSession} from "./useSyncShaftPreferencesToSelectionSession";

// Note prefix is reused with criteria due to duplication and to avoid duplicating language keys
const t = createInternationalizationPrefixer("FanSelectionCriteriaControls");


export function useMechanicalConfiguratorCriteriaControls(availableShaftDiameters: number[],
                                                          predefinedCriteria: SimulationRequestJson,
                                                          updateQueryParamPredefinedCriteriaWithoutReload: (newCriteria: SimulationRequestJson) => any) {
    const [lastUsedConfigurationSummary, setLastUsedConfigurationSummary] = useState<UsedConfigurationSummary | null>(null);

    function useLastUsedH3WhenUnAutoselected() {
        if (latest.current?.impellerHeight) {
            impellerHeightEditor.populate(latest.current?.impellerHeight);
        }
    };

    function useLastShaftDiameterWhenUnAutoselected() {
        if (latest.current?.shaftDiameter) {
            shaftDiameterEditor.populate(latest.current.shaftDiameter.toFixed(0));
        }
    };

    function useLastShaftLengthWhenUnAutoselected() {
        if (latest.current?.shaftLength) {
            shaftLengthEditor.populate(latest.current.shaftLength);
        }
    };

    function useLastScrewMWhenUnAutoselected() {
        if (latest.current?.mforShaftThread) {
            screwMEditor.populate(latest.current.mforShaftThread);
        }
        if (latest.current?.shaftThreadLength) {
            threadLengthEditor.populate(latest.current.shaftThreadLength);
        }
    };



    const {
        shaftDiameterEditor,
        shaftLengthEditor,
        screwMEditor,
        threadLengthEditor
    } = useShaftDimensionsEditors(availableShaftDiameters,
        predefinedCriteria,
        useLastShaftDiameterWhenUnAutoselected,
        useLastShaftLengthWhenUnAutoselected,
        useLastScrewMWhenUnAutoselected);


    const latest = useRef(lastUsedConfigurationSummary);
    useEffect(() => {
        latest.current = lastUsedConfigurationSummary;
    }, [lastUsedConfigurationSummary]);



    console.log("useMechanicalConfiguratorCriteriaControls", predefinedCriteria);
    const impellerHeightEditor = useOptionalMeasurementEditor(WentechMeasuringStandards.IMPELLER_HEIGHT,
        false,
        undefined,
        false,
        useLastUsedH3WhenUnAutoselected);
    const fanMountingOrientationEditor = useEditor<OneOfEditor>(() => new OneOfEditor([
        {key: "HORIZONTAL", label: t("Poziomo  (wał pionowy)")},
        {key: "VERTICAL", label: t("Pionowo (wał poziomy)")},
    ], MetronicMultiButtonOneOfEditorXxs));
    const airflowRelativeClutchMountingSideEditor = useEditor<OneOfEditor>(() => new OneOfEditor([
        {key: "CLUTCH_ON_INLET", label: t("Sprzęgło na wlocie")},
        {key: "CLUTCH_ON_OUTLET", label: t("Sprzęgło na wylocie")},
    ], MetronicMultiButtonOneOfEditorXxs));
    const supportAndClutchDiscSideEditor = useEditor<OneOfEditor>(() => new OneOfEditor([
        {key: "SUPPORTS_AND_CLUTCH_OPPOSITE_SIDES", label: t("Przeciwne strony")},
        {key: "SUPPORTS_AND_CLUTCH_SAME_SIDE", label: t("Ta sama strona")},
    ], MetronicMultiButtonOneOfEditorXxs));
    const realizeWithClutchEditor = useEditor<OneOfEditor>(() => new OneOfEditor([
        {key: "true", label: t("Ze sprzęgłem")},
        {key: "false", label: t("Bez sprzęgła")},
    ], MetronicMultiButtonOneOfEditorXxs));
    const wymiaryPrzylaczenioweZatwierdzoneEditor = useEditor<OneOfEditor>(() => new OneOfEditor([
        {key: "false", label: t("Niezatwierdzone")},
        {key: "true", label: t("Zatwierdzone")},
    ], MetronicMultiButtonOneOfEditorXxs));

    useEffect(function preopopulate() {
        const mechanicalPrefs = predefinedCriteria.mechanicalPreferences;
        if (mechanicalPrefs.expectedImpellerHeight != null) {
            impellerHeightEditor.populate(mechanicalPrefs.expectedImpellerHeight)
            impellerHeightEditor.enable();
        }
        mechanicalPrefs.fanMountingOrientationCode != null && fanMountingOrientationEditor.populate(mechanicalPrefs.fanMountingOrientationCode);
        mechanicalPrefs.airflowRelativeClutchMountingSideCode != null && airflowRelativeClutchMountingSideEditor.populate(mechanicalPrefs.airflowRelativeClutchMountingSideCode);
        mechanicalPrefs.preferredSupportAndClutchDiscSideCode != null && supportAndClutchDiscSideEditor.populate(mechanicalPrefs.preferredSupportAndClutchDiscSideCode);
        predefinedCriteria.realizeWithClutch != null && realizeWithClutchEditor.populate(predefinedCriteria.realizeWithClutch ? "true" : "false");
        wymiaryPrzylaczenioweZatwierdzoneEditor.populate(mechanicalPrefs.wymiaryPrzylaczenioweZatwierdzone ? "true" : "false");
    }, []); // Expected to be run only once when conifgurator is instantiated

    const currentlyConfiguredSimulationRequestJson: SimulationRequestJson = {
        demoAirFlow: predefinedCriteria!.demoAirFlow,
        fanFlowSpecification: predefinedCriteria!.fanFlowSpecification,
        installationSpecification: predefinedCriteria!.installationSpecification,
        manufacturingSpecification: predefinedCriteria!.manufacturingSpecification,
        mechanicalPreferences: {
            airflowRelativeClutchMountingSideCode: airflowRelativeClutchMountingSideEditor.value,
            expectedImpellerHeight: impellerHeightEditor.value,
            fanMountingOrientationCode: fanMountingOrientationEditor.value,
            preferredSupportAndClutchDiscSideCode: supportAndClutchDiscSideEditor.value,
            shaftDiameter: parseInt(shaftDiameterEditor.value!),
            shaftLength: shaftLengthEditor.value,
            customShaftThreadPreference: (screwMEditor.value === null && threadLengthEditor.value === null) ? null : {customDlugoscGwintuWCzopieWalu: threadLengthEditor.value!, customScrewM: screwMEditor.value!},
            wymiaryPrzylaczenioweZatwierdzone: wymiaryPrzylaczenioweZatwierdzoneEditor.value === "true",
        },
        softStartPresent: predefinedCriteria!.softStartPresent,
        realizeWithClutch: realizeWithClutchEditor.value === "true",
        probingOnly: false,
        fanWorkingPointQuery: predefinedCriteria.fanWorkingPointQuery,
        installationWorkingPointQuery: predefinedCriteria.installationWorkingPointQuery
    };

    useEffect(() => {
        console.log("Criteria possibly changed, updating URL")
        updateQueryParamPredefinedCriteriaWithoutReload( currentlyConfiguredSimulationRequestJson);

    }, [ JSON.stringify(currentlyConfiguredSimulationRequestJson) ]);

    useSyncShaftPreferencesToSelectionSession(
        currentlyConfiguredSimulationRequestJson.mechanicalPreferences,
        currentlyConfiguredSimulationRequestJson.realizeWithClutch
    );

    const wymiaryPrzylaczenioweZatwierdzone = wymiaryPrzylaczenioweZatwierdzoneEditor.value === "true";

    const configuratorComponent = <MechanicalConfigurator predefinedCriteria={currentlyConfiguredSimulationRequestJson}
                                                          supportAndClutchDiscSideEditor={supportAndClutchDiscSideEditor}
                                                          airflowRelativeClutchMountingSideEditor={airflowRelativeClutchMountingSideEditor}
                                                          fanMountingOrientationEditor={fanMountingOrientationEditor}
                                                          shaftDiameterEditor={shaftDiameterEditor}
                                                          shaftLengthEditor={shaftLengthEditor}
                                                          impellerHeightEditor={impellerHeightEditor}
                                                          availableShaftDiameters={availableShaftDiameters}
                                                          realizeWithClutchEditor={realizeWithClutchEditor}
                                                          currentlyConfiguredSimulationRequestJson={currentlyConfiguredSimulationRequestJson}
                                                          screwMEditor={screwMEditor}
                                                          threadLengthEditor={threadLengthEditor}
                                                          usedConfigurationSummary={lastUsedConfigurationSummary}/>;


    return {
        shaftDiameterEditor,
        shaftLengthEditor,
        impellerHeightEditor,
        fanMountingOrientationEditor,
        airflowRelativeClutchMountingSideEditor,
        supportAndClutchDiscSideEditor,
        currentlyConfiguredSimulationRequestJson,
        realizeWithClutchEditor,
        configuratorComponent,
        setLastUsedConfigurationSummary,
        wymiaryPrzylaczenioweZatwierdzone,
        wymiaryPrzylaczenioweZatwierdzoneEditor,
    }
}